import { useState, useEffect } from "react";
import api from "@/lib/api";
import { setAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const REMEMBER_KEY = "besson_admin_remember";
const LAST_EMAIL_KEY = "besson_admin_last_email";

export default function AdminLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const lastEmail = localStorage.getItem(LAST_EMAIL_KEY) || "";
      const remember = localStorage.getItem(REMEMBER_KEY) !== "0";
      if (lastEmail) setForm((f) => ({ ...f, email: lastEmail, remember }));
    } catch {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { email: form.email, password: form.password, remember: form.remember });
      setAuth(r.data.token, r.data.name);
      try {
        if (form.remember) {
          localStorage.setItem(LAST_EMAIL_KEY, form.email);
          localStorage.setItem(REMEMBER_KEY, "1");
        } else {
          localStorage.removeItem(LAST_EMAIL_KEY);
          localStorage.setItem(REMEMBER_KEY, "0");
        }
      } catch {}
      toast.success("Bienvenue !");
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion échouée");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0A1128] grain relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute -top-32 -left-20 w-96 h-96 blob-turquoise opacity-30" />
      <div className="absolute -bottom-32 right-0 w-96 h-96 blob-peach opacity-20" />
      <form onSubmit={submit} className="relative w-full max-w-sm bg-white rounded-3xl p-8 shadow-2xl">
        <img src="https://customer-assets.emergentagent.com/job_3a2b13f5-84ee-42a0-a8c4-4ac266af26a7/artifacts/8jhfiwyn_BESSONSOLUTION-logo-avatar.png" className="w-14 h-14 rounded-xl mx-auto" alt="" />
        <h1 className="display text-2xl text-center font-extrabold text-[#0A1128] mt-4">Back-office</h1>
        <p className="text-center text-sm text-[#64748B] mt-1">BESSON SOLUTIONS</p>

        <div className="mt-6 space-y-4">
          <div>
            <Label>Email</Label>
            <Input data-testid="login-email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>Mot de passe</Label>
            <Input data-testid="login-password" type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#0A1128] cursor-pointer select-none" data-testid="login-remember-label">
            <input
              type="checkbox"
              data-testid="login-remember"
              checked={form.remember}
              onChange={(e) => setForm({ ...form, remember: e.target.checked })}
              className="rounded border-black/20 text-[#06B6D4] focus:ring-[#06B6D4]"
            />
            <span>Se souvenir de moi <span className="text-[#64748B] text-xs">(session 30 jours)</span></span>
          </label>
        </div>

        <Button type="submit" disabled={loading} data-testid="login-submit" className="mt-6 w-full bg-[#0A1128] hover:bg-[#14213d] text-white">
          <Lock size={14} className="mr-2" /> {loading ? "Connexion..." : "Se connecter"}
        </Button>
      </form>
    </div>
  );
}
